/**
 * Robust Group Service - Uses fail-fast API client
 * 
 * ELIMINATES THESE REGRESSIONS:
 * - Silent failures returning empty group lists
 * - Missing auth causing mysterious "No groups found" 
 * - Manual token setup that gets forgotten
 * - 401 errors without clear cause
 */

import { getRobustApiClient } from './robustApiInstance';
import type { ApiResponse } from './robustApiClient';

export interface EntraEntity {
  id: string;
  displayName: string;
  description: string;
  type: 'group' | 'user';
  subType?: 'Security' | 'Microsoft 365';
  groupTypes?: string[];
  securityEnabled?: boolean;
  mailEnabled?: boolean;
  userPrincipalName?: string;
  mail?: string;
  isInUse?: boolean;  // Whether the group is already in use (only present in search results)
}

export interface EntraGroup extends EntraEntity {
  type: 'group';
  groupTypes: string[];
}

export interface EntraUser extends EntraEntity {
  type: 'user';
  userPrincipalName: string;
  mail?: string;
}

export interface GroupSearchResponse {
  groups: EntraEntity[];
  total: number;
  query: string;
}

export interface UserSearchResponse {
  users: EntraEntity[];
  total: number;
  query: string;
}

class RobustGroupService {
  private apiClient: any = null;

  private getApiClient() {
    if (!this.apiClient) {
      this.apiClient = getRobustApiClient();
    }
    return this.apiClient;
  }

  /**
   * FAIL-FAST: Search for Entra ID groups with automatic auth validation
   * 
   * @param query Search query (minimum 2 characters)
   * @returns Promise with group search results
   * @throws Error if auth is invalid, API is unreachable, or other issues
   */
  async searchGroups(query: string): Promise<GroupSearchResponse> {
    // Input validation
    if (!query || typeof query !== 'string') {
      throw new Error('INVALID_QUERY: Query must be a non-empty string');
    }

    const trimmedQuery = query.trim();
    if (trimmedQuery.length < 2) {
      return {
        groups: [],
        total: 0,
        query: trimmedQuery
      };
    }

    try {
      console.log(`🔍 ROBUST GROUP SEARCH: Searching for "${trimmedQuery}"`);
      
      // This will automatically validate auth and handle retries
      const response: ApiResponse<GroupSearchResponse> = await this.getApiClient().get(
        `/teams/groups/search?q=${encodeURIComponent(trimmedQuery)}`
      );

      if (!response.success) {
        throw new Error(`API_RESPONSE_ERROR: ${response.message || 'Search request failed'}`);
      }

      if (!response.data) {
        throw new Error('INVALID_RESPONSE: API returned success but no data');
      }

      const result = response.data;
      
      // Validate response structure
      if (!Array.isArray(result.groups)) {
        throw new Error('INVALID_RESPONSE_FORMAT: Expected groups array in response');
      }

      console.log(`✅ ROBUST GROUP SEARCH: Found ${result.groups.length} groups for "${trimmedQuery}"`);
      
      return {
        groups: result.groups,
        total: result.total || result.groups.length,
        query: result.query || trimmedQuery
      };

    } catch (error) {
      console.error('❌ ROBUST GROUP SEARCH: Failed:', error);
      
      // Re-throw with context for debugging
      if (error instanceof Error) {
        throw new Error(`GROUP_SEARCH_FAILED: ${error.message}`);
      } else {
        throw new Error(`GROUP_SEARCH_FAILED: Unknown error occurred during group search`);
      }
    }
  }

  /**
   * Get group details by ID
   */
  async getGroupById(groupId: string): Promise<EntraGroup> {
    if (!groupId || typeof groupId !== 'string') {
      throw new Error('INVALID_GROUP_ID: Group ID must be a non-empty string');
    }

    try {
      console.log(`🔍 ROBUST GROUP LOOKUP: Getting group ${groupId}`);
      
      const response: ApiResponse<EntraGroup> = await this.getApiClient().get(
        `/teams/groups/${encodeURIComponent(groupId)}`
      );

      if (!response.success || !response.data) {
        throw new Error(`GROUP_NOT_FOUND: Could not retrieve group ${groupId}`);
      }

      console.log(`✅ ROBUST GROUP LOOKUP: Retrieved group ${groupId}`);
      return response.data;

    } catch (error) {
      console.error(`❌ ROBUST GROUP LOOKUP: Failed for ${groupId}:`, error);
      throw new Error(`GROUP_LOOKUP_FAILED: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Validate multiple group IDs
   */
  async validateGroups(groupIds: string[]): Promise<{ valid: EntraGroup[]; invalid: string[] }> {
    if (!Array.isArray(groupIds)) {
      throw new Error('INVALID_INPUT: Group IDs must be an array');
    }

    const valid: EntraGroup[] = [];
    const invalid: string[] = [];

    for (const groupId of groupIds) {
      try {
        const group = await this.getGroupById(groupId);
        valid.push(group);
      } catch (error) {
        console.warn(`Group validation failed for ${groupId}:`, error);
        invalid.push(groupId);
      }
    }

    return { valid, invalid };
  }

  /**
   * FAIL-FAST: Search for Entra ID users with automatic auth validation
   *
   * @param query Search query (minimum 2 characters)
   * @returns Promise with user search results
   * @throws Error if auth is invalid, API is unreachable, or other issues
   */
  async searchUsers(query: string): Promise<UserSearchResponse> {
    // Input validation
    if (!query || typeof query !== 'string') {
      throw new Error('INVALID_QUERY: Query must be a non-empty string');
    }

    const trimmedQuery = query.trim();
    if (trimmedQuery.length < 2) {
      return {
        users: [],
        total: 0,
        query: trimmedQuery
      };
    }

    try {
      console.log(`🔍 ROBUST USER SEARCH: Searching for "${trimmedQuery}"`);

      // Call the combined endpoint that returns both users and groups
      const response: ApiResponse<GroupSearchResponse> = await this.getApiClient().get(
        `/teams/groups/search?q=${encodeURIComponent(trimmedQuery)}`
      );

      if (!response.success) {
        throw new Error(`API_RESPONSE_ERROR: ${response.message || 'Search request failed'}`);
      }

      if (!response.data) {
        throw new Error('INVALID_RESPONSE: API returned success but no data');
      }

      const result = response.data;

      // Filter for users only
      const users = (result.groups || []).filter((entity: EntraEntity) => entity.type === 'user');

      console.log(`✅ ROBUST USER SEARCH: Found ${users.length} users for "${trimmedQuery}"`);

      return {
        users: users as EntraUser[],
        total: users.length,
        query: result.query || trimmedQuery
      };

    } catch (error) {
      console.error('❌ ROBUST USER SEARCH: Failed:', error);

      // Re-throw with context for debugging
      if (error instanceof Error) {
        throw new Error(`USER_SEARCH_FAILED: ${error.message}`);
      } else {
        throw new Error(`USER_SEARCH_FAILED: Unknown error occurred during user search`);
      }
    }
  }

  /**
   * UNIFIED SEARCH: Search for both users and groups
   *
   * @param query Search query (minimum 2 characters)
   * @param type Optional filter for entity type
   * @returns Promise with combined search results
   * @throws Error if auth is invalid, API is unreachable, or other issues
   */
  async searchEntities(query: string, type?: 'user' | 'group'): Promise<{ entities: EntraEntity[]; total: number; query: string }> {
    // Input validation
    if (!query || typeof query !== 'string') {
      throw new Error('INVALID_QUERY: Query must be a non-empty string');
    }

    const trimmedQuery = query.trim();
    if (trimmedQuery.length < 2) {
      return {
        entities: [],
        total: 0,
        query: trimmedQuery
      };
    }

    try {
      console.log(`🔍 ROBUST ENTITY SEARCH: Searching for "${trimmedQuery}" (type: ${type || 'all'})`);

      // This endpoint returns both users and groups
      const response: ApiResponse<GroupSearchResponse> = await this.getApiClient().get(
        `/teams/groups/search?q=${encodeURIComponent(trimmedQuery)}`
      );

      if (!response.success) {
        throw new Error(`API_RESPONSE_ERROR: ${response.message || 'Search request failed'}`);
      }

      if (!response.data) {
        throw new Error('INVALID_RESPONSE: API returned success but no data');
      }

      const result = response.data;

      // Filter by type if specified
      let entities = result.groups || [];
      if (type) {
        entities = entities.filter((entity: EntraEntity) => entity.type === type);
      }

      console.log(`✅ ROBUST ENTITY SEARCH: Found ${entities.length} entities for "${trimmedQuery}"`);

      return {
        entities,
        total: entities.length,
        query: result.query || trimmedQuery
      };

    } catch (error) {
      console.error('❌ ROBUST ENTITY SEARCH: Failed:', error);

      // Re-throw with context for debugging
      if (error instanceof Error) {
        throw new Error(`ENTITY_SEARCH_FAILED: ${error.message}`);
      } else {
        throw new Error(`ENTITY_SEARCH_FAILED: Unknown error occurred during entity search`);
      }
    }
  }

  /**
   * Health check for group service
   */
  async healthCheck(): Promise<{ healthy: boolean; details: any }> {
    try {
      // Test basic group search
      const testResult = await this.searchGroups('test');

      return {
        healthy: true,
        details: {
          searchWorking: true,
          testQuery: 'test',
          groupsFound: testResult.groups.length
        }
      };
    } catch (error) {
      return {
        healthy: false,
        details: {
          error: error instanceof Error ? error.message : 'Unknown error',
          searchWorking: false
        }
      };
    }
  }
}

// Export singleton instance
export const robustGroupService = new RobustGroupService();
export default robustGroupService;